/**
 * Reading a structural action log entry, whichever snapshot format it is in.
 *
 * Two formats coexist and will for as long as the 7-day retention window holds v1 entries:
 *
 *   v1  the fields sit at the top level of original_snapshot — { oldName }, { itemName,
 *       old_unit }, { duplicateItem, masterItem }.
 *   v2  original_snapshot is an envelope. The restorable state lives in `before`, and the
 *       human-facing labels live in `meta`.
 *
 * Without this, a v2 entry renders with blank From/To values: the component looks for
 * `orig.oldName` and finds `orig.meta.oldName`. Every screen that displays a log needs the
 * same reconciliation, so it lives in one place — the duplicated copy in two components is
 * exactly the drift this whole change is about.
 */

/** @returns {number} 2 for a v2 envelope, 1 for anything older. */
export function snapshotVersion(originalSnapshot) {
  if (!originalSnapshot || typeof originalSnapshot !== 'object') return 1;
  const v = Number(originalSnapshot.schema_version);
  return Number.isInteger(v) && v >= 2 ? v : 1;
}

/**
 * Normalize a log entry into the fields the UI displays.
 *
 * Reads v2 `meta` first and falls back to the v1 top-level shape, so one render path serves
 * both. Missing values come back as undefined rather than "undefined" — a blank cell is
 * honest, a string reading "undefined" is not.
 *
 * @param {object} log  a row from GET /revert-engine/logs
 * @returns {{version:number, actionType:string, itemName?:string, oldName?:string,
 *            newName?:string, oldUnit?:string, newUnit?:string, conversionFactor?:number,
 *            duplicateName?:string, masterName?:string, isRevertEntry:boolean,
 *            revertedLogId?:number}}
 */
export function snapshotView(log) {
  const orig = (log && log.original_snapshot) || {};
  const mod = (log && log.modified_snapshot) || {};
  const version = snapshotVersion(orig);
  const meta = version >= 2 ? (orig.meta || {}) : orig;

  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '');

  return {
    version,
    actionType: log ? log.action_type : undefined,
    isRevertEntry: (log && log.action_type) === 'revert',
    revertedLogId: pick(orig.reverted_log_id, log && log.reverts_log_id),

    itemName: pick(meta.itemName, orig.itemName, meta.name),
    oldName: pick(meta.oldName, orig.oldName),
    newName: pick(meta.newName, mod.newName),

    oldUnit: pick(meta.old_unit, orig.old_unit),
    newUnit: pick(meta.new_unit, mod.new_unit),
    conversionFactor: pick(meta.conversionFactor, mod.conversionFactor),

    // v1 stored whole row objects; v2 stores just the names, because the rows themselves
    // are in `before` and are not the UI's business.
    duplicateName: pick(meta.duplicateName, orig.duplicateItem && orig.duplicateItem.name),
    masterName: pick(meta.masterName, orig.masterItem && orig.masterItem.name),

    // Item ↔ preparation conversion. Only ever v2 — the feature did not exist before the
    // envelope, so there is no v1 shape to fall back to.
    prepName: pick(meta.prepName),
    prepYield: pick(meta.prepYield),
    prepYieldUnit: pick(meta.prepYieldUnit),
    recipeCount: pick(meta.recipeCount),
    componentCount: pick(meta.componentCount),
  };
}

/**
 * The one-line summary shown in the confirmation dialog.
 *
 * Reverting is destructive and was a single unguarded click. Naming what is about to change
 * is the difference between a confirmation the user reads and one they dismiss.
 */
export function revertSummary(log) {
  const v = snapshotView(log);
  switch (v.actionType) {
    case 'rename':
      return v.oldName && v.newName
        ? `"${v.newName}" will be renamed back to "${v.oldName}".`
        : 'This rename will be undone.';
    case 'unit_change':
      return v.itemName && v.oldUnit
        ? `"${v.itemName}" will go back to ${v.oldUnit}, and every recipe, batch and preparation quantity that was rescaled will be restored.`
        : 'This unit change will be undone across every dependent quantity.';
    case 'delete':
      return v.itemName
        ? `"${v.itemName}" will be restored, along with its batches and recipe links.`
        : 'This deleted ingredient will be restored with its batches and recipe links.';
    case 'merge':
      return v.duplicateName && v.masterName
        ? `"${v.duplicateName}" will be separated back out of "${v.masterName}", and both items' quantities will be restored as they were.`
        : 'This merge will be undone and both items restored as they were.';
    case 'convert':
      // Names what actually changes for the kitchen: which stock a sale draws down.
      return v.itemName && v.prepName
        ? `"${v.itemName}" will go back to being a purchased ingredient. Sales will deduct it directly again instead of the ${v.componentCount || ''} ingredients of "${v.prepName}"${v.recipeCount ? `, across ${v.recipeCount} recipe(s)` : ''}.`.replace(/\s+/g, ' ')
        : 'This ingredient will go back to being purchased rather than made in-house.';
    case 'unlink_preparation':
      return v.itemName && v.prepName
        ? `"${v.itemName}" will be made from "${v.prepName}" again, and sales will deduct its ingredients instead of the item itself.`
        : 'This ingredient will be made from its preparation again.';
    default:
      return 'This action will be undone.';
  }
}

export default { snapshotVersion, snapshotView, revertSummary };
